<template>
  <div :class="$style.root">
    <div v-for="review in previewItems" :key="review.document_id" :class="$style.item">
      <PagePartsReviewListItem :review="review" />
    </div>
    <AtomsConsumerButton variant="secondary" block :class="$style.view_more" @click="state.isOpenModal = true">
      もっと見る
    </AtomsConsumerButton>

    <MoleculesConsumerModal v-model="state.isOpenModal" :class="$style.modal" @update:model-value="resetReviewList">
      <div v-if="state.isOpenSort" :class="$style.overlay" @click="toggleSort" />
      <div :class="$style.modal_container">
        <h3 :class="$style.modal_title">お客様の着用レビュー</h3>
        <PagePartsReviewListFilter
          :sex-item="state.sexItem"
          :height-item="state.heightItem"
          :weight-item="state.weightItem"
          :foot-item="state.footItem"
          :sort="reviewGroupVariablesRef.sort ?? 'CREATED_UNIXTIME_DESC'"
          :review-count="reviewRet?.count ?? 0"
          :is-open-sort="state.isOpenSort"
          :is-foot-category="isFootCategory?.isReviewGroupInFootCategory ?? false"
          @update:sex-item="updateSexItem"
          @update:height-item="updateHeightItem"
          @update:weight-item="updateWeightItem"
          @update:foot-item="updateFootItem"
          @update:sort="updateSort"
          @update:open-sort="state.isOpenSort = $event"
        />
        <div v-for="review in reviewRet?.items" :key="review.document_id" :class="$style.item">
          <PagePartsReviewListItem :review="review" />
        </div>
        <div v-show="!reviewRet?.items?.length" :class="$style.item">
          該当するコンテンツが見つかりませんでした。条件を変更して再度お試しください。
        </div>
        <AtomsConsumerButton
          v-show="showAllReviews"
          variant="secondary"
          block
          :class="$style.view_more"
          @click="viewMoreReviews"
        >
          もっと見る
        </AtomsConsumerButton>
      </div>
    </MoleculesConsumerModal>
  </div>
</template>

<script setup lang="ts">
import { Foot } from "@tential/ec-gql-schema/master/user"
import { useIsReviewGroupInFootCategoryQuery } from "~/gql/urql.generated"
import { sexSelect, heightFilterSelect, weightFilterSelect, footFilterSelect } from "~/utils/const/review"
import type { CommonCheckBoxItem } from "~/types/review"
import type { ApiDataReviewResponse } from "~/types/server/api/data/review"
import type { ApiDataReviewQuery } from "~/server/api/data/review_groups/[reviewGroupId].get"

type State = {
  isOpenModal: boolean
  isOpenSort: boolean
  sexItem: CommonCheckBoxItem[]
  heightItem: CommonCheckBoxItem[]
  weightItem: CommonCheckBoxItem[]
  footItem: CommonCheckBoxItem[]
}

const props = defineProps<{ reviewGroupId: string }>()

const state = ref<State>({
  isOpenModal: false,
  isOpenSort: false,
  sexItem: sexSelect.map((item) => ({
    key: item.name,
    value: item.id,
    checked: false,
  })),
  heightItem: heightFilterSelect.map((item) => ({
    key: item.key,
    value: item.value,
    checked: false,
  })),
  weightItem: weightFilterSelect.map((item) => ({
    key: item.key,
    value: item.value,
    checked: false,
  })),
  footItem: footFilterSelect.map((item) => ({
    key: item.name,
    value: item.id,
    checked: false,
  })),
})

const { data: isFootCategory, executeQuery: isFootCategoryExec } = await useIsReviewGroupInFootCategoryQuery({
  requestPolicy: "cache-first",
  variables: { reviewGroupId: props.reviewGroupId },
  pause: true,
})

const reviewGroupVariablesRef = ref<ApiDataReviewQuery>({
  page: 1,
  limit: 20,
  initial: undefined,
  sex: [],
  height_min: undefined,
  height_max: undefined,
  weight_min: undefined,
  weight_max: undefined,
  sort: "CREATED_UNIXTIME_DESC",
})

/** モーダル表示用のレビューリスト */
const reviewRet = ref<Omit<ApiDataReviewResponse, "previewItems">>()
/** プレビュー表示用のレビュー3件 */
const previewItems = ref<ApiDataReviewResponse["previewItems"]>([])

/** レビューを取得する関数 初回ロード時に引数initialをtrueにする */
const reviewExec = async ({ initial = false }: { initial: boolean }): Promise<ApiDataReviewResponse> => {
  // zod側は Boolean(value) で 判定するため、false にするためには undefined を渡す
  reviewGroupVariablesRef.value.initial = !initial ? undefined : true

  const response = await $fetch<ApiDataReviewResponse>(`/api/data/review_groups/${props.reviewGroupId}`, {
    method: "GET",
    query: reviewGroupVariablesRef.value,
  })

  return response
}
/** レビューを取得して初期化する関数 初回ロード時に引数initialをtrueにする */
const initializeReviewData = async (initial: boolean = false): Promise<void> => {
  const response = await reviewExec({ initial })
  // 初回表示時のみpreviewItemsを更新
  if (initial) previewItems.value = response.previewItems

  reviewRet.value = response
}

const { data: ssrReviewRet } = await useLazyAsyncData("usePublicReviewGroupOneQuery", async () => {
  await isFootCategoryExec()
  if (isFootCategory.value?.isReviewGroupInFootCategory && reviewGroupVariablesRef.value) {
    reviewGroupVariablesRef.value.sex = []
    reviewGroupVariablesRef.value.foot = []
  }
  await initializeReviewData(true)

  return { isFootCategory: isFootCategory.value }
})
if (!isFootCategory.value) isFootCategory.value = ssrReviewRet.value?.isFootCategory

const showAllReviews = computed(() => {
  return reviewRet.value?.pageInfo?.hasNextPage
})

const viewMoreReviews = async (): Promise<void> => {
  reviewGroupVariablesRef.value.page += 1
  const newReviews = await reviewExec({ initial: false })

  if (reviewRet.value && newReviews.items) {
    reviewRet.value = {
      ...newReviews,
      items: [...(reviewRet.value.items || []), ...newReviews.items],
    }
  }
}

/** 性別Filterの更新 */
const updateSexItem = async (value: CommonCheckBoxItem[]): Promise<void> => {
  state.value.sexItem = value

  const checkedSexValues = state.value.sexItem.filter((item) => item.checked).map((item) => item.value)

  if (reviewGroupVariablesRef.value.sex)
    reviewGroupVariablesRef.value.sex = checkedSexValues.length === 0 ? [] : checkedSexValues

  reviewGroupVariablesRef.value.page = 1
  await nextTick(initializeReviewData)
}
/** 身長Filterの更新 */
const updateHeightItem = async (value: CommonCheckBoxItem[]): Promise<void> => {
  state.value.heightItem = value

  const checkedHeightValues = state.value.heightItem.filter((item) => item.checked).map((item) => item.value)
  const selectedHeightFilter = heightFilterSelect.filter((item) => checkedHeightValues.includes(item.value))

  const gteNum = selectedHeightFilter.length > 0 ? Math.min(...selectedHeightFilter.map((item) => item.gte)) : undefined
  const lteNum = selectedHeightFilter.length > 0 ? Math.max(...selectedHeightFilter.map((item) => item.lte)) : undefined

  reviewGroupVariablesRef.value.height_min = gteNum
  reviewGroupVariablesRef.value.height_max = lteNum

  reviewGroupVariablesRef.value.page = 1
  await nextTick(initializeReviewData)
}
/** 体重Filterの更新 */
const updateWeightItem = async (value: CommonCheckBoxItem[]): Promise<void> => {
  state.value.weightItem = value

  const checkedWeightValues = state.value.weightItem.filter((item) => item.checked).map((item) => item.value)
  const selectedWeightFilters = weightFilterSelect.filter((item) => checkedWeightValues.includes(item.value))

  const gteNum =
    selectedWeightFilters.length > 0 ? Math.min(...selectedWeightFilters.map((item) => item.gte)) : undefined
  const lteNum =
    selectedWeightFilters.length > 0 ? Math.max(...selectedWeightFilters.map((item) => item.lte)) : undefined

  reviewGroupVariablesRef.value.weight_min = gteNum
  reviewGroupVariablesRef.value.weight_max = lteNum

  reviewGroupVariablesRef.value.page = 1
  await nextTick(initializeReviewData)
}
/** 足のサイズFilterの更新 */
const updateFootItem = async (value: CommonCheckBoxItem[]): Promise<void> => {
  state.value.footItem = value

  const checkedFootValues = state.value.footItem.filter((item) => item.checked).map((item) => item.value)
  const isFoot = (value: string): value is Foot => {
    return Object.keys(Foot).includes(value)
  }
  const validFootValues = checkedFootValues.filter(isFoot)
  if (reviewGroupVariablesRef.value.foot) reviewGroupVariablesRef.value.foot = validFootValues
  reviewGroupVariablesRef.value.page = 1
  await nextTick(initializeReviewData)
}

const updateSort = async (sort: ApiDataReviewQuery["sort"]): Promise<void> => {
  reviewGroupVariablesRef.value.sort = sort
  reviewGroupVariablesRef.value.page = 1
  await nextTick(initializeReviewData)
}

const resetReviewList = async (modelValue: boolean): Promise<void> => {
  if (modelValue === false) {
    reviewGroupVariablesRef.value.page = 1
    await nextTick(initializeReviewData)
  }
}

const toggleSort = (): void => {
  state.value.isOpenSort = !state.value.isOpenSort
}
</script>

<style module lang="scss">
.root {
  @include container-md;

  .item {
    box-sizing: border-box;
    padding: 1.875rem 0;
    border-bottom: 1px solid $primary-20;
  }

  .view_more {
    margin-top: 1.25rem;
  }

  .modal_container {
    padding: 1.25rem;
    overflow-y: hidden;
    @include container-md;

    @include md {
      min-width: 1000px;
    }

    .modal_title {
      font-size: $font-size-15;
      margin-bottom: 1rem;

      @include md {
        font-size: $font-size-18;
      }
    }
  }

  .overlay {
    position: fixed;
    inset: 0px;
  }
}
</style>
